/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * API Client Utility
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/utils/apiClient.js
import axios from 'axios';

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
});

// Proprietary software identifier - DO NOT REMOVE
apiClient.__PROPRIETARY__ = {
    author: 'Srivatsan Sk',
    copyright: '© 2025 Srivatsan Sk. All rights reserved.',
    license: 'PROPRIETARY',
    notice: 'This software is protected by copyright law.',
    version: '1.0.0',
    confidential: true
};

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for handling token refresh
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Log network errors for debugging
        if (!error.response) {
            console.error('Network Error:', {
                message: error.message,
                code: error.code,
                config: {
                    url: originalRequest?.url,
                    method: originalRequest?.method,
                    baseURL: originalRequest?.baseURL
                }
            });
        }

        const authEndpoints = [
            'user/login/',
            'user/register/',
            'user/activate/',
            'user/password-reset-request/',
            'user/password-reset-confirm/'
        ];

        const isAuthEndpoint = authEndpoints.some(endpoint =>
            originalRequest?.url?.includes(endpoint)
        );

        // If this is an auth endpoint, just reject the error without redirecting
        if (isAuthEndpoint) {
            console.log('Auth endpoint failed - not redirecting');
            return Promise.reject(error);
        }

        // If the error is due to an expired token (401) and we haven't tried refreshing yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');

                if (refreshToken) {
                    // Try to refresh the token
                    const response = await axios.post(
                        `${apiClient.defaults.baseURL}token/refresh/`,
                        { refresh: refreshToken }
                    );

                    if (response.data.access) {
                        // Store the new access token
                        localStorage.setItem('accessToken', response.data.access);

                        // Update the auth header for the original request
                        originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;

                        // Retry the original request
                        return apiClient(originalRequest);
                    }
                } else {
                    // No refresh token available, redirect to login
                    console.log('No refresh token - redirecting to login');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    window.location.href = '/login?session_expired=true';
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                // If refresh token is invalid or expired, redirect to login
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');

                // Force redirect to login page
                window.location.href = '/login?session_expired=true';
            }
        } else if (error.response?.status === 401) {
            // If this is a retry and still 401, redirect to login
            console.log('Retry failed - redirecting to login');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login?session_expired=true';
        }

        return Promise.reject(error);
    }
);

// API request helpers
const api = {
    // Auth requests
    auth: {
        // User Registration
        register: async (userData) => {
            const response = await apiClient.post('user/register/', userData);
            return response.data;
        },

        // User Login
        login: async (credentials) => {
            const response = await apiClient.post('user/login/', credentials);
            return response.data;
        },
        logout: async (refreshToken) => {
            const response = await apiClient.post('user/logout/', { refresh: refreshToken });
            return response.data;
        },

        // Account Activation
        activate: async (uidb64, token) => {
            const response = await apiClient.get(`user/activate/${uidb64}/${token}/`);
            return response.data;
        },

        // Resend Activation Email
        resendActivation: async (email) => {
            const response = await apiClient.post('user/resend-activation/', { email });
            return response.data;
        },

        // Password Reset Request (not implemented in our backend yet)
        requestPasswordReset: async (email) => {
            const response = await apiClient.post('user/password-reset-request/', { email });
            return response.data;
        },

        // Password Reset Confirmation (not implemented in our backend yet)
        confirmPasswordReset: async (resetData) => {
            const response = await apiClient.post('user/password-reset-confirm/', resetData);
            return response.data;
        },

        // Change Password
        changePassword: async (passwordData) => {
            const response = await apiClient.post('user/change-password/', {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password,
                from_login: passwordData.from_login || false
            });
            return response.data;
        },

        // Token Refresh (usually handled by interceptor, but available if needed manually)
        refreshToken: async (refreshToken) => {
            const response = await axios.post(
                `${apiClient.defaults.baseURL}token/refresh/`,
                { refresh: refreshToken }
            );
            return response.data;
        }
    },

    // User Profile requests
    users: {
        getDashboardStats: async () => {
            const response = await apiClient.get('user/dashboard');
            return response.data;
        },
        // Get Current User Profile
        getProfile: async () => {
            const response = await apiClient.get('user/profile/');
            return response.data;
        },

        // Update User Profile
        updateProfile: async (profileData) => {
            const response = await apiClient.put('user/profile/update/', profileData);
            return response.data;
        },
        changePassword: async (passwordData) => {
            const response = await apiClient.post('user/profile/change-password/', passwordData);
            return response.data;
        },

        leaderBoard: async () => {
            const response = await apiClient.get('user/leaderboard/');
            return response.data;
        },
        getWorkshopLeaderboard: async (workshopCode) => {
            const response = await apiClient.get(`user/leaderboard/workshops/${workshopCode}/`);
            return response.data;
        },
        userProfile: async (userId) => {
            const response = await apiClient.get(`user/leaderboard/${userId}/`);
            return response.data;
        }

    },

    // Course requests
    courses: {
        // List All Courses
        getAll: async (filters = {}) => {
            const response = await apiClient.get('user/courses/', { params: filters });
            return response.data;
        },

        // Get Course Details
        getById: async (courseId) => {
            const response = await apiClient.get(`user/courses/${courseId}/`);
            return response.data;
        },

        // Enroll in Course
        enroll: async (courseId, payload = {}) => {
            const response = await apiClient.post(`user/courses/${courseId}/enroll/`, payload);
            return response.data;
        },

        // Get Course Progress
        getProgress: async (courseId) => {
            const response = await apiClient.get(`user/courses/${courseId}/progress/`);
            return response.data;
        },

        // Get Detailed Course Structure (modules & content)
        getStructure: async (courseId) => {
            const response = await apiClient.get(`user/courses/${courseId}/structure/`);
            return response.data;
        },

        // Get All Enrolled Courses
        getEnrolled: async () => {
            const response = await apiClient.get('user/enrolled-courses/');
            return response.data;
        },
        getUserProgress: async () => {
            const response = await apiClient.get('user/enrolled-courses/progress');
            return response.data;
        }
    },

    // Content (learning) requests
    content: {
        // Get Content Details
        getById: async (contentId) => {
            const response = await apiClient.get(`user/content/${contentId}/`);
            return response.data;
        },

        // Mark Content Complete
        markComplete: async (contentId) => {
            const response = await apiClient.post(`user/content/${contentId}/complete/`);
            return response.data;
        },

        // Track Content Progress
        trackProgress: async (contentId, timeSpent) => {
            const response = await apiClient.post(`user/content/${contentId}/progress/`, { time_spent: timeSpent });
            return response.data;
        }
    },

    // Quiz requests
    quizzes: {
        // Get Module Quiz
        getModuleQuiz: async (moduleId) => {
            const response = await apiClient.get(`user/modules/${moduleId}/quiz/`);
            return response.data;
        },
        getFinalQuiz: async (courseId) => {
            const response = await apiClient.get(`user/courses/${courseId}/final-quiz/`);
            return response.data;
        },

        // Submit Module Quiz
        submitQuiz: async (moduleId, answers, violations = 0) => {
            const response = await apiClient.post(`user/modules/${moduleId}/quiz/submit/`, { answers, violations });
            console.log('Submitting quiz with violations:', violations);
            return response.data;
        },
        submitFinalQuiz: async (courseId, answers, violations = 0) => {
            const response = await apiClient.post(`user/courses/${courseId}/final-quiz/submit/`, { answers, violations });
            return response.data;
        },
        getQuizCooldown: async (courseId) => {
            const response = await apiClient.get(`user/quiz-cooldown/${courseId}/`);
            return response.data;
        }
    },
    notifications: {
        getRecent: async (limit = 50, include_read = true) => {
            const params = new URLSearchParams({
                limit: limit.toString(),
                include_read: include_read.toString()
            });
            const response = await apiClient.get(`user/notifications/?${params}`);
            return response.data;
        },
        markAsRead: async (notificationId) => {
            const response = await apiClient.put(`user/notifications/mark-read/${notificationId}/`);
            return response.data;
        },
        markAllAsRead: async () => {
            const response = await apiClient.put('user/notifications/mark-all-read/');
            return response.data;
        },
        deleteNotification: async (notificationId) => {
            const response = await apiClient.delete(`user/notifications/delete/${notificationId}/`);
            return response.data;
        }
    },
    achievements: {
        getAll: async () => {
            const response = await apiClient.get('user/achievements/available/');
            return response.data;
        }
    },

    // VM requests
    vm: {
        // Get VM Status
        getStatus: async () => {
            const response = await apiClient.get('user/vm/status/');
            return response.data;
        },

        // Launch Kali VM
        launchKali: async () => {
            const response = await apiClient.post('user/vm/launch-kali/');
            return response.data;
        },

        // Launch Juice Shop VM
        launchJuiceShop: async () => {
            const response = await apiClient.post('user/vm/launch-juice-shop/');
            return response.data;
        },

        // Stop Kali VM
        stopKali: async () => {
            const response = await apiClient.post('user/vm/stop-kali/');
            return response.data;
        },

        // Stop Juice Shop VM
        stopJuiceShop: async () => {
            const response = await apiClient.post('user/vm/stop-juice-shop/');
            return response.data;
        },

        // Get Juice Shop Status
        getJuiceShopStatus: async () => {
            const response = await apiClient.get('user/vm/juice-shop-status/');
            return response.data;
        },

        // Cleanup Expired Sessions
        cleanupSessions: async () => {
            const response = await apiClient.post('user/vm/cleanup-sessions/');
            return response.data;
        }
    },

    // Payment requests
    payment: {
        // Create order for course purchase
        createOrder: async (courseId, couponCode = null) => {
            const response = await apiClient.post('user/payment/create-order/', {
                course_id: courseId,
                coupon_code: couponCode
            });
            return response.data;
        },

        // Verify payment after Razorpay success
        verifyPayment: async (paymentData) => {
            const response = await apiClient.post('user/payment/verify/', paymentData);
            return response.data;
        },

        // Validate coupon code
        validateCoupon: async (courseId, couponCode) => {
            const response = await apiClient.post('user/payment/validate-coupon/', {
                course_id: courseId,
                coupon_code: couponCode
            });
            return response.data;
        },

        // Get purchase history
        getPurchaseHistory: async () => {
            const response = await apiClient.get('user/payment/history/');
            return response.data;
        },

        // Get lab access status
        getLabAccessStatus: async () => {
            const response = await apiClient.get('user/payment/lab-access/');
            return response.data;
        },

        // Get course pricing
        getCoursePricing: async (courseId) => {
            const response = await apiClient.get(`user/payment/course-pricing/${courseId}/`);
            return response.data;
        },

        // Download invoice
        downloadInvoice: async (orderId) => {
            const response = await apiClient.get(`user/payment/invoice/${orderId}/`, {
                responseType: 'blob'
            });
            return response;
        },

        // Get order details
        getOrderDetails: async (orderId) => {
            const response = await apiClient.get(`user/payment/order/${orderId}/`);
            return response.data;
        }
    },

    verification: {
        getById: async (verificationId) => {
            const response = await apiClient.get(`user/verify/${verificationId}/`);
            return response.data;
        }
    }
};

export default api;